import React, { useEffect, useMemo, useRef, useState } from 'react';
import AuthSection from '../AuthSection.jsx';
import { resolveStaticUrl } from '../../utils/staticAssets.js';
import { useLucideIcons } from '../../hooks/usePageAssets.js';
import RestaurantsSection from './RestaurantsSection.jsx';
import UsersSection from './UsersSection.jsx';
import MotivosSection from './MotivosSection.jsx';
import OperatingExceptionsSection from './OperatingExceptionsSection.jsx';
import ProcessesSection from './ProcessesSection.jsx';
import ArticlesSection from './ArticlesSection.jsx';
import RecipesSection from './RecipesSection.jsx';
import WarmersSection from './WarmersSection.jsx';
import CategoriesSection from './CategoriesSection.jsx';
import UsageMetricsSection from './UsageMetricsSection.jsx';
import ForecastSettingsSection from './ForecastSettingsSection.jsx';
import DivisionsSection from './DivisionsSection.jsx';
import AreasSection from './AreasSection.jsx';
import AzucarGlassSection from './AzucarGlassSection.jsx';
import AdditionalInfoSection from './AdditionalInfoSection.jsx';
import GamificationSection from './GamificationSection.jsx';
import DaypartsSection from './DaypartsSection.jsx';
import MaintenanceSection from './MaintenanceSection.jsx';

const ADMIN_SECTIONS = {
  maintenance: { id: 'maintenance', label: 'Mantenimiento', icon: 'construction', component: MaintenanceSection },
  restaurants: { id: 'restaurants', label: 'Restaurantes', icon: 'building', component: RestaurantsSection },
  users: { id: 'users', label: 'Usuarios', icon: 'users', component: UsersSection },
  divisions: { id: 'divisions', label: 'Divisiones', icon: 'layout-grid', component: DivisionsSection },
  areas: { id: 'areas', label: 'Áreas', icon: 'map-pin', component: AreasSection },
  processes: { id: 'processes', label: 'Procesos', icon: 'flame', component: ProcessesSection },
  warmers: { id: 'warmers', label: 'Warmers', icon: 'thermometer', component: WarmersSection },
  articles: { id: 'articles', label: 'Artículos', icon: 'package', component: ArticlesSection },
  recipes: { id: 'recipes', label: 'Recetas', icon: 'chef-hat', component: RecipesSection },
  categories: { id: 'categories', label: 'Categorías', icon: 'layers', component: CategoriesSection },
  'motivos-ajuste': {
    id: 'motivos-ajuste',
    label: 'Motivos de Ajuste',
    icon: 'sliders-horizontal',
    component: MotivosSection
  },
  'operating-exceptions': {
    id: 'operating-exceptions',
    label: 'Excepciones',
    icon: 'clock-7',
    component: OperatingExceptionsSection
  },
  metrics: { id: 'metrics', label: 'Métricas', icon: 'bar-chart-3', component: UsageMetricsSection },
  gamification: { id: 'gamification', label: 'Gamificación', icon: 'trophy', component: GamificationSection },
  'forecast-settings': {
    id: 'forecast-settings',
    label: 'Pronóstico AI',
    icon: 'wand-sparkles',
    component: ForecastSettingsSection,
    description: 'Configura los parámetros del pronóstico automático.'
  },
  dayparts: {
    id: 'dayparts',
    label: 'Dayparts',
    icon: 'clock-7',
    component: DaypartsSection,
    description: 'Define rangos horarios globales para pronósticos.'
  },
  'azucar-glass': {
    id: 'azucar-glass',
    label: 'Azúcar Glass',
    icon: 'droplet',
    component: AzucarGlassSection,
    description: 'Define el uso de AI.FORECAST o equivalencias para procesos Azucar Glass.'
  },
  'additional-info': {
    id: 'additional-info',
    label: 'Info adicional',
    icon: 'file-text',
    component: AdditionalInfoSection,
    description: 'Contenido extra por proceso para mostrar en el dashboard.'
  }
};

const SIDEBAR_GROUPS = [
  {
    id: 'administration',
    title: 'Administración',
    sectionIds: ['restaurants', 'users', 'divisions', 'areas']
  },
  {
    id: 'data-management',
    title: 'Gestión de Datos',
    sectionIds: ['processes', 'warmers', 'categories', 'articles', 'recipes']
  },
  {
    id: 'metrics',
    title: 'Métricas',
    sectionIds: ['metrics', 'gamification']
  },
  {
    id: 'settings',
    title: 'Configuración',
    sectionIds: ['maintenance', 'forecast-settings', 'dayparts', 'azucar-glass', 'additional-info', 'motivos-ajuste', 'operating-exceptions']
  }
];

const DEFAULT_SECTION = SIDEBAR_GROUPS[0]?.sectionIds?.[0] || 'restaurants';
const ALL_SECTIONS = Object.values(ADMIN_SECTIONS);
const FALLBACK_SECTION = ADMIN_SECTIONS[DEFAULT_SECTION] || ALL_SECTIONS[0] || null;
const NAV_GROUPS = SIDEBAR_GROUPS.map((group) => ({
  ...group,
  sections: group.sectionIds.map((sectionId) => ADMIN_SECTIONS[sectionId]).filter(Boolean)
})).filter((group) => group.sections.length > 0);

const AdminApp = ({ bootstrap }) => {
  const [activeSection, setActiveSection] = useState(FALLBACK_SECTION?.id || DEFAULT_SECTION);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [toasts, setToasts] = useState([]);
  const toastTimeouts = useRef(new Map());
  const homeHref = bootstrap?.routes?.dashboard
    || bootstrap?.routes?.home
    || bootstrap?.routes?.landing
    || '/';
  const user = bootstrap?.user_profile;
  const userInitial = (user?.name || user?.email || '?').charAt(0).toUpperCase();
  const isSuperAdmin = Boolean(bootstrap?.is_super_admin);

  useLucideIcons([
    activeSection,
    sidebarOpen,
    toasts.map((toast) => `${toast.type}-${toast.id}`).join('|')
  ]);

  useEffect(() => () => {
    toastTimeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
    toastTimeouts.current.clear();
  }, []);

  const removeToast = (toastId) => {
    setToasts((current) => current.filter((toast) => toast.id !== toastId));
    const timeoutId = toastTimeouts.current.get(toastId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      toastTimeouts.current.delete(toastId);
    }
  };

  const handleNotify = (payload) => {
    if (!payload?.message) {
      return;
    }
    const toastId = `toast-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const duration = payload.duration ?? (payload.type === 'error' ? 6200 : 4500);
    const nextToast = {
      id: toastId,
      type: payload.type || 'info',
      message: payload.message,
      duration
    };

    setToasts((current) => [...current, nextToast]);

    if (duration !== 0) {
      const timeoutId = window.setTimeout(() => removeToast(toastId), duration);
      toastTimeouts.current.set(toastId, timeoutId);
    }
  };

  const resetToasts = () => {
    toastTimeouts.current.forEach((timeoutId) => clearTimeout(timeoutId));
    toastTimeouts.current.clear();
    setToasts([]);
  };

  const activeDefinition = useMemo(
    () => ADMIN_SECTIONS[activeSection] || FALLBACK_SECTION,
    [activeSection]
  );

  const renderActiveSection = () => {
    if (!activeDefinition) {
      return null;
    }
    const SectionComponent = activeDefinition.component;
    if (!SectionComponent) {
      return null;
    }
    return <SectionComponent onNotify={handleNotify} />;
  };

  return (
    <div id="admin-page" className="admin-page">
      <header className="main-header">
        <div className="header-left">
          <a href={homeHref}>
            <img src={resolveStaticUrl('images/logo.png')} alt="Tim Hortons" className="header-logo" />
          </a>
          <h1 className="system-title">Panel de Administración</h1>
        </div>
        <div className="header-right">
          <AuthSection showAdminLink={false} />
        </div>
      </header>
      <button
        className="admin-sidebar-toggle"
        type="button"
        onClick={() => setSidebarOpen((value) => !value)}
      >
        <i data-lucide="menu" />
      </button>
      <div className={`admin-layout ${sidebarOpen ? 'open' : ''}`}>
        <aside className="admin-sidebar" id="admin-sidebar">
          <div className="sidebar-content">
            <div className="mobile-only sidebar-context">
              {isSuperAdmin ? (
                <div className="admin-info context-item">
                  <div className="context-content">
                    <div className="context-label">Acceso</div>
                    <div className="admin-badge mobile-admin">
                      <i data-lucide="crown" />
                      <span>Super Admin</span>
                    </div>
                  </div>
                </div>
              ) : null}
              {user ? (
                <div className="context-item">
                  <div className="context-content">
                    <div className="context-label">Sesión activa</div>
                    <div className="user-info">
                      {user?.picture ? (
                        <img src={user.picture} alt={user.name || user.email} className="user-avatar" />
                      ) : (
                        <span className="user-avatar user-avatar-fallback">{userInitial}</span>
                      )}
                      <div className="user-info-text">
                        <span className="user-name">{user?.name || user?.email || 'Usuario'}</span>
                        {user?.email && user?.name ? <span className="user-email">{user.email}</span> : null}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="context-item mobile-actions-admin">
                <div className="context-content">
                  <div className="context-label">Navegación</div>
                  <a href={homeHref} className="mobile-action-btn">
                    <i data-lucide="arrow-left" /> Volver al Modelo
                  </a>
                </div>
              </div>
            </div>
            <nav className="admin-nav">
              <div className="nav-section back-button-section desktop-only">
                <ul className="nav-menu">
                  <li className="nav-item">
                    <a href={homeHref} className="nav-link back-button">
                      <span className="nav-icon"><i data-lucide="arrow-left" /></span>
                      <span className="nav-text">Volver al Modelo</span>
                    </a>
                  </li>
                </ul>
              </div>
              {NAV_GROUPS.map((group) => (
                <div className="nav-section" key={group.id}>
                  <h3 className="nav-section-title">{group.title}</h3>
                  <ul className="nav-menu">
                    {group.sections.map((section) => (
                      <li className="nav-item" key={section.id}>
                        <button
                          type="button"
                          className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
                          data-section={section.id}
                          aria-current={activeSection === section.id ? 'page' : undefined}
                          onClick={() => {
                            setActiveSection(section.id);
                            setSidebarOpen(false);
                            resetToasts();
                          }}
                        >
                          <span className="nav-icon">
                            <i data-lucide={section.icon} />
                          </span>
                          <span className="nav-text">{section.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>
        <div
          className={`admin-sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
        <main className="admin-content">
          {renderActiveSection()}
        </main>
      </div>
      <AdminToastStack toasts={toasts} onDismiss={removeToast} />
    </div>
  );
};

const AdminToastStack = ({ toasts, onDismiss }) => (
  <div className="admin-toast-container" aria-live="polite" aria-atomic="true">
    {toasts.map((toast) => (
      <div
        key={toast.id}
        className={`admin-toast admin-toast-${toast.type || 'info'}`}
        role={toast.type === 'error' ? 'alert' : 'status'}
      >
        <div className="toast-icon">
          <i data-lucide={toast.type === 'error' ? 'alert-octagon' : toast.type === 'warning' ? 'alert-triangle' : toast.type === 'success' ? 'check-circle' : 'info'} />
        </div>
        <div className="toast-body">
          <p className="toast-message">{toast.message}</p>
        </div>
        <button
          type="button"
          className="toast-close"
          onClick={() => onDismiss?.(toast.id)}
          aria-label="Cerrar notificación"
        >
          <i data-lucide="x" />
        </button>
      </div>
    ))}
  </div>
);

export default AdminApp;
